import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Save, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    logo_url: "",
    website: "",
    description: "",
    is_active: true,
  });

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/providers`);
      setProviders(response.data);
    } catch (error) {
      toast.error("Sağlayıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await axios.put(`${API}/providers/${editingProvider.id}`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("Sağlayıcı güncellendi");
      } else {
        await axios.post(`${API}/providers`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("Sağlayıcı eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchProviders();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu sağlayıcıyı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/providers/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Sağlayıcı silindi");
      fetchProviders();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      slug: provider.slug,
      logo_url: provider.logo_url || "",
      website: provider.website || "",
      description: provider.description || "",
      is_active: provider.is_active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProvider(null);
    setFormData({
      name: "",
      slug: "",
      logo_url: "",
      website: "",
      description: "",
      is_active: true,
    });
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  return (
    <AdminLayout title="Sağlayıcılar">
      <div className="space-y-6" data-testid="admin-providers">
        <div className="flex justify-between items-center">
          <p className="text-slate-600">Tüm sağlayıcıları (anbieter) buradan yönetebilirsiniz.</p>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-electric-500 hover:bg-electric-600"
            data-testid="add-provider-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Sağlayıcı
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map((provider) => (
              <Card key={provider.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {provider.logo_url ? (
                        <img 
                          src={provider.logo_url} 
                          alt={provider.name} 
                          className="w-12 h-12 object-contain rounded-lg bg-white border border-slate-100"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <span className="font-bold text-slate-400">{provider.name.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-navy-900">{provider.name}</h3>
                        <p className="text-sm text-slate-500">/{provider.slug}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${provider.is_active ? "bg-signal-100 text-signal-700" : "bg-slate-100 text-slate-500"}`}>
                      {provider.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                  
                  {provider.website && (
                    <a 
                      href={provider.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-electric-600 mt-3 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Website
                    </a>
                  )}
                  
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(provider)}
                      data-testid={`edit-provider-${provider.id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Düzenle
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(provider.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-provider-${provider.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Sil
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingProvider ? "Sağlayıcı Düzenle" : "Yeni Sağlayıcı"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sağlayıcı Adı *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        name: e.target.value,
                        slug: editingProvider ? formData.slug : generateSlug(e.target.value),
                      });
                    }}
                    placeholder="Vattenfall"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug (URL)</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="vattenfall"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://logo.clearbit.com/vattenfall.de"
                />
                <p className="text-xs text-slate-500">Örn: https://logo.clearbit.com/domain.de</p>
              </div>

              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://vattenfall.de"
                />
              </div>

              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Sağlayıcı hakkında kısa bilgi"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Aktif</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" className="bg-electric-500 hover:bg-electric-600">
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
